import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

interface PortalThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

/**
 * Applies the theme to the document based on the mode setting
 */
export function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;

  if (mode === 'system') {
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  } else {
    root.classList.toggle('dark', mode === 'dark');
  }
}

/**
 * Customer Portal theme store using Zustand with persistence
 */
export const usePortalThemeStore = create<PortalThemeState>()(
  persist(
    (set) => ({
      mode: 'system',

      setMode: (mode: ThemeMode) => {
        applyTheme(mode);
        set({ mode });
      },
    }),
    {
      name: 'portal-theme-storage',
      onRehydrateStorage: () => (state) => {
        // Apply theme when state is rehydrated from localStorage
        if (state) {
          applyTheme(state.mode);
        }
      },
    }
  )
);

/**
 * Initialize theme and set up system preference listener
 * Call this once when the portal app mounts
 */
export function initializePortalTheme(): () => void {
  const { mode } = usePortalThemeStore.getState();

  // Apply initial theme
  applyTheme(mode);

  // Listen for system preference changes (only matters when mode is 'system')
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleChange = () => {
    const currentMode = usePortalThemeStore.getState().mode;
    if (currentMode === 'system') {
      applyTheme('system');
    }
  };

  mediaQuery.addEventListener('change', handleChange);

  // Return cleanup function
  return () => {
    mediaQuery.removeEventListener('change', handleChange);
  };
}
